/**
 * Decoding for the chat-flow turn stream: SSE frames in, Buddy message parts out.
 * Frame names and payload shapes are the widget's wire contract (apps/chat-v1
 * `realtime/sse-adapter.ts`) — `aichat` carries a whole reply, `token` carries a
 * delta, and the card types carry their payload at `content.message[0]`.
 */
import type { UIMessage } from '@ai-sdk/react'
import { parseSSEFrames } from 'v2/utils/sse-frames'
import type { Part } from './utils'

export interface BuddyFlowFrame {
  type: string
  turn_id?: string
  gen?: number
  message_id?: string
  content?: Record<string, unknown>
}

/** Card buttons rendered as quick replies; tapping one sends its text as the next message. */
export interface BuddyFlowQuickReplies {
  title?: string
  description?: string
  replies: string[]
}

export const QUICK_REPLIES_PART_TYPE = 'data-buddy-quick-replies'

export const FLOW_SIGNALS = {
  AI_STARTED: 'AI_CHATBOT_STARTED',
  AI_ENDED: 'AI_CHATBOT_ENDED',
  CONVERSATION_CLOSED: 'CONVERSATION_CLOSED',
  AGENT_ASSIGNING: 'AGENT_ASSIGNING',
} as const

interface FlowCardButton {
  text?: string
}

interface FlowCard {
  title?: string
  description?: string
  buttons?: FlowCardButton[]
}

interface FlowFileRef {
  file_url?: string
  file_name?: string
  file_type?: string
}

const firstCard = (frame: BuddyFlowFrame): FlowCard | undefined =>
  (frame.content?.message as FlowCard[] | undefined)?.[0]

export const readFlowSignalName = (frame: BuddyFlowFrame): string =>
  String(frame.content?.event_name ?? frame.content?.eventName ?? '')

export const readFlowErrorMessage = (frame: BuddyFlowFrame): string =>
  String(frame.content?.message ?? frame.content?.code ?? 'Buddy stream failed')

/**
 * Split an SSE buffer into decoded frames. `[DONE]` is the stream terminator and
 * is reported separately; unparseable frames are skipped rather than thrown, so
 * one malformed payload cannot kill a turn that is otherwise fine.
 */
export const readFlowFrames = (
  buffer: string,
): { frames: BuddyFlowFrame[]; rest: string; done: boolean } => {
  const { frames: raw, rest } = parseSSEFrames(buffer)
  const frames: BuddyFlowFrame[] = []
  let done = false
  for (const frame of raw) {
    if (frame.data === '[DONE]') {
      done = true
      continue
    }
    try {
      const parsed = JSON.parse(frame.data) as BuddyFlowFrame
      if (parsed && typeof parsed.type === 'string') frames.push(parsed)
    } catch {
      // A malformed frame is skipped; the rest of the turn still renders.
    }
  }
  return { frames, rest, done }
}

const isStreaming = (part: Part): boolean => (part as { state?: string }).state === 'streaming'

const withPart = (message: UIMessage, part: Part): UIMessage => ({
  ...message,
  parts: [...message.parts, part],
})

/** Flip every still-streaming text part to done — the turn produced no more of it. */
export const closeFlowStreamingParts = (message: UIMessage): UIMessage => ({
  ...message,
  parts: message.parts.map((part) =>
    part.type === 'text' && isStreaming(part) ? { ...part, state: 'done' as const } : part,
  ),
})

const appendDelta = (message: UIMessage, delta: string): UIMessage => {
  const last = message.parts[message.parts.length - 1]
  if (last?.type === 'text' && isStreaming(last)) {
    const parts = [...message.parts]
    parts[parts.length - 1] = { ...last, text: last.text + delta, state: 'streaming' }
    return { ...message, parts }
  }
  return withPart(message, { type: 'text', text: delta, state: 'streaming' })
}

/**
 * `aichat` is the whole reply. When deltas already opened a bubble for this
 * generation it replaces that text and closes it, so a stream that sends both
 * renders one answer rather than two.
 */
const applyWholeReply = (message: UIMessage, text: string): UIMessage => {
  const last = message.parts[message.parts.length - 1]
  if (last?.type === 'text' && isStreaming(last)) {
    const parts = [...message.parts]
    parts[parts.length - 1] = { ...last, text, state: 'done' }
    return { ...message, parts }
  }
  return withPart(message, { type: 'text', text, state: 'done' })
}

export const applyBuddyFlowFrame = (message: UIMessage, frame: BuddyFlowFrame): UIMessage => {
  switch (frame.type) {
    case 'token': {
      const delta = String(frame.content?.delta ?? '')
      return delta ? appendDelta(message, delta) : message
    }
    case 'aichat': {
      const text = String(frame.content?.description ?? '')
      return text ? applyWholeReply(message, text) : message
    }
    case 'turn_done':
      return closeFlowStreamingParts(message)
    case 'chat_button':
    case 'chat_menu_button': {
      const card = firstCard(frame)
      const replies = (card?.buttons ?? [])
        .map((button) => String(button?.text ?? ''))
        .filter(Boolean)
      if (!replies.length) return message
      return withPart(message, {
        type: QUICK_REPLIES_PART_TYPE,
        data: { title: card?.title, description: card?.description, replies },
      } as Part)
    }
    case 'chat_multi_product': {
      // Carousels render as plain text in v1 — the panel has no card rail.
      const items = ((frame.content?.message as Array<{ items?: FlowCard[] }> | undefined)?.[0]
        ?.items ?? []) as FlowCard[]
      const lines = items.map((item) => item?.title).filter(Boolean)
      return lines.length ? applyWholeReply(message, lines.join('\n')) : message
    }
    case 'file': {
      const files = (frame.content?.message as FlowFileRef[] | undefined) ?? []
      return files.reduce<UIMessage>(
        (acc, file) =>
          file?.file_url
            ? withPart(acc, {
                type: 'file',
                url: file.file_url,
                filename: file.file_name,
                mediaType: file.file_type || 'application/octet-stream',
              })
            : acc,
        message,
      )
    }
    case 'progress': {
      // agent-runner relays each action as started → completed | failed. Fold it into
      // the same tool parts the aiassist path renders, so the panel shows "Searched
      // docs" style activity lines for the flow too (labels come from TOOL_LABELS).
      const c = frame.content ?? {}
      if (c.kind !== 'tool' || typeof c.id !== 'string') return message
      const toolType = `tool-${c.id}`
      const callId = typeof c.toolCallId === 'string' ? c.toolCallId : `${c.id}-${message.parts.length}`
      if (c.state === 'started') {
        return withPart(message, {
          type: toolType,
          toolCallId: callId,
          state: 'input-available',
          input: {},
        } as Part)
      }
      if (c.state !== 'completed' && c.state !== 'failed') return message
      const idx = message.parts.findIndex(
        (part) => part.type === toolType && (part as { toolCallId?: string }).toolCallId === callId,
      )
      const finished = {
        type: toolType,
        toolCallId: callId,
        state: c.state === 'failed' ? 'output-error' : 'output-available',
        output: {},
        ...(c.state === 'failed' ? { errorText: String(c.label ?? 'failed') } : {}),
      } as Part
      if (idx === -1) return withPart(message, finished)
      const parts = [...message.parts]
      parts[idx] = { ...(parts[idx] as object), ...(finished as object) } as Part
      return { ...message, parts }
    }
    default:
      // signal / error / directive carry conversation state, not content — the hook
      // reads those off the same frames.
      return message
  }
}
